import { useCallback, useEffect, useMemo } from 'react';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { isNilOrEmpty, isNilOrEmptyString } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getMessage } from '@gef-ui/localization/messages';

import { ComponentLoader } from 'components';
import fetch from 'features/common/fetch';
import { gtmDashboardSectionNames, gtmFilterNames } from 'features/views/dashboard/constants';
import { useButtonClickDataLayer, usePlusMoreButtonClickDataLayer } from 'features/views/dashboard/hooks';
import { fetchTakenOrInProgressLeads } from 'features/views/dashboard/marketPlace/actions';
import { toggleButtonGroupValues } from 'features/views/dashboard/marketPlace/constants';
import { getTakenOrInProgressLeadsSplitted, getToggleValue } from 'features/views/dashboard/marketPlace/selectors';
import { setToggleValue } from 'features/views/dashboard/marketPlace/slice';
import { quickFilter } from 'features/views/marketPlace/constants';
import Lead from 'pages/MarketPlace/components/Lead';

import PlusMoreButton from './PlusMoreButton';

export const TakenLeads = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const pushToClickPlusMoreButtonDataLayer = usePlusMoreButtonClickDataLayer();
	const pushToClickButtonDataLayer = useButtonClickDataLayer();

	const toggleValue = useSelector(getToggleValue);
	const { leadsToShow, originalLength } = useSelector(getTakenOrInProgressLeadsSplitted);
	const plusMoreValue = originalLength - 3;

	useEffect(() => {
		dispatch(fetchTakenOrInProgressLeads());
	}, [dispatch]);

	const handleRedirect = useCallback(() => {
		const filterType = toggleValue === toggleButtonGroupValues.DRAFT ? gtmFilterNames.DRAFT : gtmFilterNames.TAKEN;
		pushToClickPlusMoreButtonDataLayer({
			value: gtmDashboardSectionNames.TAKEN_LEADS,
			filter_type: filterType,
		})();

		navigate({
			pathname: '/market-place',
			...(toggleValue === toggleButtonGroupValues.DRAFT && { search: `quickFilter=${quickFilter.IN_PROGRESS}` }),
		});
	}, [navigate, pushToClickPlusMoreButtonDataLayer, toggleValue]);

	const handleToggle = useCallback(
		(e, toggleValue) => {
			if (toggleValue !== null) {
				const buttonText =
					toggleValue === toggleButtonGroupValues.DRAFT ? gtmFilterNames.DRAFT : gtmFilterNames.TAKEN;

				pushToClickButtonDataLayer({
					value: gtmDashboardSectionNames.TAKEN_LEADS,
					button_text: buttonText,
				})();

				dispatch(setToggleValue(toggleValue));
				dispatch(fetchTakenOrInProgressLeads());
			}
		},
		[dispatch, pushToClickButtonDataLayer],
	);

	const noResults = useMemo(
		() => (
			<Grid size={12}>
				<Alert severity="info">{getMessage('dashboard:marketPlace.noTakenLeads')}</Alert>
			</Grid>
		),
		[],
	);

	const selectedButtonSx = {
		'&.Mui-selected, &.Mui-selected:hover': {
			background: (theme) => theme.palette.primary.main,
			color: (theme) => theme.palette.common.white,
		},
	};

	const showDoughnutChartTakenLeads = useCallback(
		(lead) => {
			if (toggleValue === toggleButtonGroupValues.TAKEN) {
				const doesntHaveFeedback = isNilOrEmptyString(lead?.finishTypeId);
				return doesntHaveFeedback;
			} else {
				const hasInProgressFeedback = lead?.statusId !== 3 && !isNilOrEmptyString(lead?.finishTypeId);
				return hasInProgressFeedback;
			}
		},
		[toggleValue],
	);

	return (
		<Stack spacing={1}>
			<Stack
				direction="row"
				sx={{
					alignItems: 'center',
					justifyContent: 'space-between',
				}}
			>
				<Typography sx={{ fontWeight: 'bold' }} variant="body1">
					{getMessage('dashboard:marketPlace.takenLeadsTitle')}
				</Typography>
			</Stack>
			<ComponentLoader fetchStorePath={fetch.constants.urls.takenOrInProgressLeads} forceInlineError>
				<Paper sx={{ p: 3, borderRadius: 1, backgroundColor: (theme) => theme.palette.background[100] }}>
					<ToggleButtonGroup
						size="small"
						exclusive
						value={toggleValue}
						onChange={handleToggle}
						sx={{
							mb: 2,
							width: 'fit-content',
							height: '30px',
						}}
					>
						<ToggleButton sx={selectedButtonSx} value={toggleButtonGroupValues.TAKEN}>
							{getMessage('dashboard:marketPlace.taken')}
						</ToggleButton>
						<ToggleButton sx={selectedButtonSx} value={toggleButtonGroupValues.DRAFT}>
							{getMessage('dashboard:marketPlace.draft')}
						</ToggleButton>
					</ToggleButtonGroup>
					<Stack spacing={2}>
						<Grid container>
							<Grid size={12}>
								<Grid container spacing={1}>
									{isNilOrEmpty(leadsToShow)
										? noResults
										: leadsToShow.map((lead) => (
												<Grid
													size={{
														xs: 12,
														lg: 4,
													}}
												>
													<Lead
														lead={lead}
														availableInMarketPlace={false}
														showDoughnutChart={showDoughnutChartTakenLeads(lead)}
													/>
												</Grid>
										  ))}
								</Grid>
							</Grid>
						</Grid>
						<PlusMoreButton value={plusMoreValue} onClick={handleRedirect} />
					</Stack>
				</Paper>
			</ComponentLoader>
		</Stack>
	);
};

export default TakenLeads;
